import React, { useState, useEffect } from 'react';

const Achievements = ({ sessions, tasks }) => {
  const [achievements, setAchievements] = useState([]);
  const [streaks, setStreaks] = useState({
    current: 0,
    longest: 0,
    lastStudyDate: null
  });

  const achievementDefinitions = [
    {
      id: 'first_session',
      title: 'Getting Started',
      description: 'Complete your first study session',
      icon: '🌱',
      requirement: (sessions) => sessions.length >= 1
    },
    {
      id: 'early_bird',
      title: 'Early Bird',
      description: 'Study before 8 AM',
      icon: '🌅',
      requirement: (sessions) => sessions.some(s => new Date(s.completedAt).getHours() < 8)
    },
    {
      id: 'night_owl',
      title: 'Night Owl',
      description: 'Study after 10 PM',
      icon: '🦉',
      requirement: (sessions) => sessions.some(s => new Date(s.completedAt).getHours() >= 22)
    },
    {
      id: 'marathon',
      title: 'Marathon Runner',
      description: 'Study for 3+ hours in one day',
      icon: '🏃‍♂️',
      requirement: (sessions) => {
        const dailyTotals = {};
        sessions.forEach(s => {
          const date = s.date;
          dailyTotals[date] = (dailyTotals[date] || 0) + s.duration;
        });
        return Object.values(dailyTotals).some(total => total >= 3 * 60 * 60);
      }
    },
    {
      id: 'consistency_king',
      title: 'Consistency King',
      description: 'Study for 7 days in a row',
      icon: '👑',
      requirement: () => streaks.longest >= 7
    },
    {
      id: 'century_club',
      title: 'Century Club',
      description: 'Complete 100 study sessions',
      icon: '💯',
      requirement: (sessions) => sessions.length >= 100
    },
    {
      id: 'time_master',
      title: 'Time Master',
      description: 'Accumulate 100 hours of study time',
      icon: '⏰',
      requirement: (sessions) => {
        const total = sessions.reduce((sum, s) => sum + s.duration, 0);
        return total >= 100 * 60 * 60;
      }
    },
    {
      id: 'pomodoro_pro',
      title: 'Pomodoro Pro',
      description: 'Complete 50 Pomodoro sessions',
      icon: '🍅',
      requirement: (sessions) => sessions.filter(s => s.type === 'pomodoro').length >= 50
    },
    {
      id: 'task_terminator',
      title: 'Task Terminator',
      description: 'Complete 25 tasks',
      icon: '✅',
      requirement: (sessions, tasks) => tasks.filter(t => t.completed).length >= 25
    },
    {
      id: 'multitasker',
      title: 'Multitasker',
      description: 'Study 5 different subjects in one day',
      icon: '🧠',
      requirement: (sessions) => {
        const dailySubjects = {};
        sessions.forEach(s => {
          const date = s.date;
          if (!dailySubjects[date]) dailySubjects[date] = new Set();
          dailySubjects[date].add(s.subject);
        });
        return Object.values(dailySubjects).some(subjects => subjects.size >= 5);
      }
    },
    {
      id: 'weekend_warrior',
      title: 'Weekend Warrior',
      description: 'Study on both Saturday and Sunday',
      icon: '⚔️',
      requirement: (sessions) => {
        const weekendDays = sessions.filter(s => {
          const day = new Date(s.completedAt).getDay();
          return day === 0 || day === 6; // Sunday or Saturday
        });
        const uniqueWeekendDays = new Set(weekendDays.map(s => new Date(s.completedAt).getDay()));
        return uniqueWeekendDays.size >= 2;
      }
    },
    {
      id: 'focus_master',
      title: 'Focus Master',
      description: 'Complete a 2+ hour study session without breaks',
      icon: '🎯',
      requirement: (sessions) => sessions.some(s => s.duration >= 2 * 60 * 60)
    }
  ];

  const calculateStreaks = () => {
    if (sessions.length === 0) {
      return { current: 0, longest: 0, lastStudyDate: null };
    }

    // Get unique study dates, sorted
    const studyDates = [...new Set(sessions.map(s => s.date))]
      .sort((a, b) => new Date(a) - new Date(b));

    if (studyDates.length === 0) {
      return { current: 0, longest: 0, lastStudyDate: null };
    }

    let currentStreak = 1;
    let longestStreak = 1;
    let lastDate = new Date(studyDates[0]);

    for (let i = 1; i < studyDates.length; i++) {
      const currentDate = new Date(studyDates[i]);
      const daysDiff = Math.floor((currentDate - lastDate) / (1000 * 60 * 60 * 24));

      if (daysDiff === 1) {
        currentStreak++;
        longestStreak = Math.max(longestStreak, currentStreak);
      } else if (daysDiff > 1) {
        currentStreak = 1;
      }

      lastDate = currentDate;
    }

    // Check if current streak is still active (studied today or yesterday)
    const today = new Date();
    const lastStudyDate = new Date(studyDates[studyDates.length - 1]);
    const daysSinceLastStudy = Math.floor((today - lastStudyDate) / (1000 * 60 * 60 * 24));

    const isStreakActive = daysSinceLastStudy <= 1;
    const finalCurrentStreak = isStreakActive ? currentStreak : 0;

    return {
      current: finalCurrentStreak,
      longest: longestStreak,
      lastStudyDate: studyDates[studyDates.length - 1]
    };
  };

  const checkAchievements = () => {
    const newAchievements = [];
    const existingIds = new Set(achievements.map(a => a.id));

    achievementDefinitions.forEach(def => {
      if (!existingIds.has(def.id) && def.requirement(sessions, tasks)) {
        newAchievements.push({
          ...def,
          unlockedAt: new Date().toISOString(),
          isNew: true
        });
      }
    });

    if (newAchievements.length > 0) {
      const updatedAchievements = [...achievements, ...newAchievements];
      setAchievements(updatedAchievements);
      localStorage.setItem('achievements', JSON.stringify(updatedAchievements));

      // Show notification for new achievements
      newAchievements.forEach(achievement => {
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('🏆 Achievement Unlocked!', {
            body: `${achievement.icon} ${achievement.title}: ${achievement.description}`,
            icon: '/favicon.ico'
          });
        }
      });
    }
  };

  const markAsRead = (achievementId) => {
    const updatedAchievements = achievements.map(a =>
      a.id === achievementId ? { ...a, isNew: false } : a
    );
    setAchievements(updatedAchievements);
    localStorage.setItem('achievements', JSON.stringify(updatedAchievements));
  };

  useEffect(() => {
    // Load achievements from localStorage
    const savedAchievements = localStorage.getItem('achievements');
    if (savedAchievements) {
      setAchievements(JSON.parse(savedAchievements));
    }
  }, []);

  useEffect(() => {
    const newStreaks = calculateStreaks();
    setStreaks(newStreaks);
    checkAchievements();
  }, [sessions, tasks]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getStreakEmoji = (streak) => {
    if (streak >= 30) return '🔥🔥🔥';
    if (streak >= 14) return '🔥🔥';
    if (streak >= 7) return '🔥';
    if (streak >= 3) return '⚡';
    return '✨';
  };

  const getCompletionPercentage = () => {
    return Math.round((achievements.length / achievementDefinitions.length) * 100);
  };

  const getTotalStats = () => {
    const totalTime = sessions.reduce((sum, s) => sum + s.duration, 0);
    const completedTasks = tasks.filter(t => t.completed).length;
    const pomodoroSessions = sessions.filter(s => s.type === 'pomodoro').length;

    return {
      totalSessions: sessions.length,
      totalTime,
      completedTasks,
      pomodoroSessions,
      uniqueSubjects: new Set(sessions.map(s => s.subject)).size
    };
  };

  const stats = getTotalStats();

  return (
    <div className="achievements">
      <div className="achievements-header">
        <h2>🏆 Achievements & Streaks</h2>
        <div className="completion-badge">
          {getCompletionPercentage()}% Complete
        </div>
      </div>

      <div className="streaks-section">
        <h3>🔥 Study Streaks</h3>
        <div className="streaks-grid">
          <div className="streak-card current">
            <div className="streak-emoji">{getStreakEmoji(streaks.current)}</div>
            <div className="streak-number">{streaks.current}</div>
            <div className="streak-label">Current Streak</div>
            <div className="streak-subtitle">
              {streaks.current > 0 ? 'Keep it up!' : 'Start your streak today!'}
            </div>
          </div>
          <div className="streak-card longest">
            <div className="streak-emoji">👑</div>
            <div className="streak-number">{streaks.longest}</div>
            <div className="streak-label">Longest Streak</div>
            <div className="streak-subtitle">Personal record</div>
          </div>
        </div>
      </div>

      <div className="stats-overview">
        <h3>📊 Study Statistics</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-icon">📚</span>
            <span className="stat-value">{stats.totalSessions}</span>
            <span className="stat-label">Total Sessions</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">⏰</span>
            <span className="stat-value">{formatTime(stats.totalTime)}</span>
            <span className="stat-label">Total Time</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">✅</span>
            <span className="stat-value">{stats.completedTasks}</span>
            <span className="stat-label">Tasks Done</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">🍅</span>
            <span className="stat-value">{stats.pomodoroSessions}</span>
            <span className="stat-label">Pomodoros</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">🎯</span>
            <span className="stat-value">{stats.uniqueSubjects}</span>
            <span className="stat-label">Subjects</span>
          </div>
        </div>
      </div>

      <div className="achievements-section">
        <h3>🏅 Achievements</h3>
        <div className="achievements-grid">
          {achievementDefinitions.map(def => {
            const isUnlocked = achievements.some(a => a.id === def.id);
            const achievement = achievements.find(a => a.id === def.id);
            const isNew = achievement?.isNew;

            return (
              <div
                key={def.id}
                className={`achievement-card ${isUnlocked ? 'unlocked' : 'locked'} ${isNew ? 'new' : ''}`}
                onClick={() => isNew && markAsRead(def.id)}
              >
                <div className="achievement-icon">
                  {isUnlocked ? def.icon : '🔒'}
                </div>
                <div className="achievement-content">
                  <h4 className="achievement-title">{def.title}</h4>
                  <p className="achievement-description">{def.description}</p>
                  {isUnlocked && achievement && (
                    <div className="achievement-date">
                      Unlocked: {new Date(achievement.unlockedAt).toLocaleDateString()}
                    </div>
                  )}
                  {isNew && (
                    <div className="new-badge">NEW!</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="progress-section">
        <h3>📈 Progress to Next Achievement</h3>
        <div className="next-achievements">
          {achievementDefinitions
            .filter(def => !achievements.some(a => a.id === def.id))
            .slice(0, 3)
            .map(def => (
              <div key={def.id} className="next-achievement">
                <div className="next-icon">{def.icon}</div>
                <div className="next-content">
                  <h4>{def.title}</h4>
                  <p>{def.description}</p>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Achievements;